import { useEffect, useMemo, useState } from 'react';
export { chatterActifSurLeJeu } from '@taki/data-model';
import type { TakiSdk } from '@taki/sdk';
import type { ChatterDuGabarit } from '@taki/ui';

/**
 * LE CHATTER DE L'OBJET AFFICHE, vu du gabarit (chantier `chatter`, 05/10/2026,
 * retour 8 du fondateur : « une conversation attachee a un objet MyTaki,
 * visible depuis cet objet », l'esprit du chatter d'Odoo). Contrat :
 * `docs/socle/chatter.md`.
 *
 * CE QUE CE MODULE FAIT, ET RIEN D'AUTRE :
 *  1. il NOMME l'objet affiche par une ANCRE (une fiche, une porte, une app) ;
 *  2. il LIT le nombre de messages de son chatter par le SDK du socle, UNE
 *     fois par objet ouvert (un `get` de la conversation, un `count()` serveur
 *     de ses messages), jamais en ecoutant toutes les conversations ;
 *  3. il OUVRE le centre de communication sur « Ici » avec cette ancre.
 *
 * CE QU'IL NE FAIT PAS : il n'ecrit aucun message, ne cree aucune conversation.
 * Un chatter n'existe qu'a partir du premier message (`ouvrirChatter` du socle,
 * appele par le centre au premier envoi) : zero document vide. Les evenements
 * systeme (« Statut : brouillon -> valide ») sont ecrits par une Function
 * (`functions/src/chatter/`), que le client ne peut pas imiter (rules).
 *
 * LE SOCLE PEUT ETRE ABSENT : `chatterDe` arrive avec la branche
 * `core/conversations`. Le bouton se rend quand meme, eteint, avec la raison
 * (G16) : un controle qui apparait et disparait selon la branche montee est
 * exactement ce que le gabarit interdit.
 */

/**
 * L'EVENEMENT QUI OUVRE LE CENTRE, jumeau de `EVENEMENT_OUVRIR_CENTRE` dans
 * `packages/shell/src/centre/ouvrirCentre.ts`. Recopie et non importe :
 * `@taki/app-runtime` ne depend pas de `@taki/shell` (c'est le shell qui monte
 * l'ecran). Le detail est `{ source: 'ici', ancre }` : le centre choisit la
 * source « Ici » et y lit le chatter de cette ancre.
 */
export const EVENEMENT_OUVRIR_CENTRE = 'taki:centre:ouvrir';

/**
 * L'EVENEMENT QUE LE CENTRE EMET QUAND UN MESSAGE PART dans un chatter
 * (`detail: { cle }`, la cle de l'ancre) : le bouton relit alors son compte.
 * Sans lui, le compte est celui de l'ouverture de l'objet, ce qui suffit.
 */
export const EVENEMENT_CHATTER_ECRIT = 'taki:chatter:ecrit';

/** L'ancre d'un chatter telle que le gabarit la connait : l'objet, et de quoi le nommer. */
export type AncreDuChatter =
  | { family: string; recordId: string; appId: string; doorId: string; titre: string }
  | { appId: string; doorId: string; titre: string }
  | { appId: string; titre: string };

/** L'ancre telle que le SDK la lit (`AncreDeConversation`, stricte : rien d'autre que ces clefs). */
export type AncreDuSocle = { family: string; recordId: string } | { appId: string; doorId?: string };

export function ancreDuSocle(ancre: AncreDuChatter): AncreDuSocle {
  if ('family' in ancre) return { family: ancre.family, recordId: ancre.recordId };
  if ('doorId' in ancre) return { appId: ancre.appId, doorId: ancre.doorId };
  return { appId: ancre.appId };
}

/** La cle de l'ancre, la meme que `cleDeChatter` du socle : `fiche:...`, `porte:...`, `app:...`. */
export function cleDAncre(ancre: AncreDuChatter): string {
  if ('family' in ancre) return `fiche:${ancre.family}/${ancre.recordId}`;
  if ('doorId' in ancre) return `porte:${ancre.appId}/${ancre.doorId}`;
  return `app:${ancre.appId}`;
}

/** Le `title` du bouton : ce qu'il ouvre. Le mot visible reste « Chatter ». */
export function libelleDuChatter(ancre: AncreDuChatter): string {
  if ('family' in ancre) return `Chatter de la fiche ${ancre.titre}`;
  if ('doorId' in ancre) return `Chatter de la porte ${ancre.titre}`;
  return `Chatter de l'app ${ancre.titre}`;
}

/** Ouvre le centre de communication sur « Ici », pour cette ancre. */
export function ouvrirLeCentreSurIci(ancre: AncreDuChatter): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(EVENEMENT_OUVRIR_CENTRE, { detail: { source: 'ici', ancre } }),
  );
}

/**
 * LE CHATTER D'UNE FICHE EST DISPONIBLE A L'ECRAN : le hook du gabarit
 * l'annonce tant qu'il le fournit (une fiche ouverte dont la base a le chatter
 * allume), et le retire en se demontant. La fenetre de communication en tire
 * son bandeau « Fiche ouverte » (R10 du soir du 05/10/2026) : pas de chatter
 * fourni, pas de bandeau. Le registre vit sur `window` pour qu'un ecouteur
 * arrive apres l'annonce sans la manquer.
 */
export const EVENEMENT_CHATTER_FICHE = 'taki:chatter:fiche';

type FenetreAvecChatters = { __takiChattersDeFiche?: Map<string, number> };

/** Les cles `fiche:...` dont le chatter est fourni par un ecran monte. */
export function chattersDeFicheAnnonces(): ReadonlySet<string> {
  if (typeof window === 'undefined') return new Set();
  return new Set((window as unknown as FenetreAvecChatters).__takiChattersDeFiche?.keys() ?? []);
}

function annoncerChatterDeFiche(cle: string, present: boolean): void {
  if (typeof window === 'undefined') return;
  const registre = ((window as unknown as FenetreAvecChatters).__takiChattersDeFiche ??= new Map());
  const n = (registre.get(cle) ?? 0) + (present ? 1 : -1);
  if (n > 0) registre.set(cle, n); else registre.delete(cle);
  window.dispatchEvent(new CustomEvent(EVENEMENT_CHATTER_FICHE, { detail: { cle, present } }));
}

/** Ce que ce module demande au socle : retrouver un chatter, compter ses messages. */
export interface PortDuChatter {
  chatterDe(ancre: AncreDuSocle): Promise<{ id: string } | null>;
  compterMessages(conversationId: string): Promise<number>;
}

export const RAISON_SOCLE_ABSENT =
  "le socle conversations n'est pas monte (sdk.conversations.chatterDe absent)";

/**
 * Le port, tire du SDK a l'execution : `chatterDe` n'existe que sur la branche
 * du socle conversations. `null` tant qu'il n'y est pas.
 */
export function portDuChatter(sdk: TakiSdk): PortDuChatter | null {
  const conversations = sdk.conversations as unknown as {
    chatterDe?: (ancre: AncreDuSocle) => Promise<{ id: string } | null>;
  };
  if (typeof conversations.chatterDe !== 'function') return null;
  return {
    chatterDe: (ancre) => conversations.chatterDe!(ancre),
    compterMessages: (conversationId) => sdk.messages.count({ acces: { conversation: conversationId } }),
  };
}

/** Un chatter lu : son nombre de messages (`0` quand il n'existe pas encore), ou la raison de l'echec. */
export async function lireLeChatter(
  port: PortDuChatter,
  ancre: AncreDuChatter,
): Promise<{ compte: number } | { raison: string }> {
  try {
    const conversation = await port.chatterDe(ancreDuSocle(ancre));
    if (conversation === null) return { compte: 0 };
    return { compte: await port.compterMessages(conversation.id) };
  } catch (cause) {
    const message = cause instanceof Error ? cause.message : String(cause);
    return { raison: `le chatter n'a pas pu etre lu (${message})` };
  }
}

/**
 * LE HOOK DU GABARIT : la prop `chatter` de `ScreenLayout` pour cette ancre.
 * `null` sans ancre (un brouillon, un modele : pas d'objet en base). Une
 * lecture par ancre, et une de plus quand le centre dit qu'un message est
 * parti dans ce chatter.
 */
export function useChatterDuGabarit(sdk: TakiSdk, ancreDemandee: AncreDuChatter | null): ChatterDuGabarit | null {
  // VAGUE DU SOIR, R9 : on ne chatte QUE sur une fiche. Une ancre de porte ou
  // d'app ne donne plus de bouton (le socle la refuse : `ChatterHorsFiche`).
  const ancre = ancreDemandee !== null && 'family' in ancreDemandee ? ancreDemandee : null;
  const cle = ancre === null ? null : cleDAncre(ancre);
  const port = useMemo(() => portDuChatter(sdk), [sdk]);
  const [lu, setLu] = useState<{ cle: string; compte: number | null; raison: string | null }>({
    cle: '',
    compte: null,
    raison: null,
  });
  const [tour, setTour] = useState(0);

  useEffect(() => {
    if (cle === null || ancre === null || port === null) return;
    let vivant = true;
    void lireLeChatter(port, ancre).then((resultat) => {
      if (!vivant) return;
      setLu('compte' in resultat
        ? { cle, compte: resultat.compte, raison: null }
        : { cle, compte: null, raison: resultat.raison });
    });
    return () => { vivant = false; };
    // `ancre` change d'identite a chaque rendu ; sa cle dit quand relire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cle, port, tour]);

  useEffect(() => {
    if (cle === null) return;
    const relire = (event: Event): void => {
      const detail = (event as CustomEvent<{ cle?: string } | undefined>).detail;
      if (detail?.cle === undefined || detail.cle === cle) setTour((n) => n + 1);
    };
    window.addEventListener(EVENEMENT_CHATTER_ECRIT, relire);
    return () => window.removeEventListener(EVENEMENT_CHATTER_ECRIT, relire);
  }, [cle]);

  useEffect(() => {
    if (cle === null || !cle.startsWith('fiche:')) return;
    annoncerChatterDeFiche(cle, true);
    return () => annoncerChatterDeFiche(cle, false);
  }, [cle]);

  if (ancre === null || cle === null) return null;
  const courant = lu.cle === cle ? lu : null;
  const raison = port === null ? RAISON_SOCLE_ABSENT : courant?.raison ?? null;
  return {
    compte: courant?.compte ?? null,
    label: libelleDuChatter(ancre),
    ...(raison === null ? {} : { raison }),
    onOuvrir: () => ouvrirLeCentreSurIci(ancre),
  };
}
